import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi, deballer } from '@/shared/api/client';
import { useSession } from '@/shared/auth/sessionStore';

/**
 * Appartenance : une entreprise dont l'utilisateur est membre, avec son role.
 */
export interface Appartenance {
  entrepriseId: string;
  raisonSociale: string;
  utilisateurId: string;
  role: string;
}

/**
 * Prefixe de cache lie a l'entreprise active (§4).
 *
 * TOUTE cle de requete d'une donnee metier commence par ce prefixe. Deux
 * entreprises ne peuvent donc jamais partager une entree de cache, meme
 * transitoirement — ce qui rend l'affichage croise structurellement impossible,
 * sans dependre du vidage de cache au changement.
 *
 * Utilisation : `queryKey: [...useCleEntreprise(), 'clients', { page }]`
 */
export function useCleEntreprise(): readonly ['entreprise', string] {
  const entrepriseActive = useSession((etat) => etat.entrepriseActive);
  return ['entreprise', entrepriseActive ?? 'aucune'] as const;
}

/** Entreprises de l'utilisateur connecte. Alimente le selecteur d'entreprise. */
export function useMesEntreprises() {
  const utilisateurId = useSession((etat) => etat.utilisateurId);

  return useQuery({
    // Cle liee a l'UTILISATEUR et non a l'entreprise : cette liste ne doit pas
    // disparaitre du cache au moment ou l'on change d'entreprise, sans quoi le
    // selecteur se viderait pendant la bascule.
    queryKey: ['utilisateur', utilisateurId, 'entreprises'],
    enabled: utilisateurId !== null,
    staleTime: 5 * 60_000,
    queryFn: () => deballer(clientApi.GET('/v1/auth/mes-entreprises', {})) as Promise<Appartenance[]>,
  });
}

/** Modules actives pour l'entreprise courante. Pilote la navigation (§3). */
export function useModulesActives() {
  const entrepriseActive = useSession((etat) => etat.entrepriseActive);

  return useQuery({
    queryKey: ['entreprise', entrepriseActive ?? 'aucune', 'modules'],
    enabled: entrepriseActive !== null,
    // Les modules changent rarement : inutile de reinterroger a chaque navigation.
    staleTime: 10 * 60_000,
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/entreprises/{entrepriseId}/modules', {
          params: { path: { entrepriseId: entrepriseActive as string } },
        }),
      ) as Promise<{ cleModule: string; actif: boolean; ordre: number }[]>,
  });
}

/**
 * Active ou desactive des modules.
 *
 * L'API attend la LISTE COMPLETE, pas un delta : elle remplace l'etat d'activation
 * de l'entreprise par celui qu'on lui envoie. Le composant appelant transmet donc
 * toujours l'ensemble des modules, chacun avec son drapeau.
 *
 * L'invalidation qui suit est large a dessein : les activations pilotent la
 * navigation ET le routeur (§3). Une entree de menu qui subsisterait apres la
 * desactivation de son module renverrait vers une route qui n'existe plus.
 */
export function useEnregistrerModules() {
  const entrepriseActive = useSession((etat) => etat.entrepriseActive);
  const client = useQueryClient();

  return useMutation({
    mutationFn: (modules: { cleModule: string; actif: boolean; ordre: number }[]) =>
      deballer(
        clientApi.PUT('/v1/entreprises/{entrepriseId}/modules', {
          params: { path: { entrepriseId: entrepriseActive as string } },
          body: modules,
        }),
      ) as Promise<{ cleModule: string; actif: boolean; ordre: number }[]>,

    onSuccess: (etat) => {
      client.setQueryData(['entreprise', entrepriseActive ?? 'aucune', 'modules'], etat);
      void client.invalidateQueries({ queryKey: ['entreprise', entrepriseActive ?? 'aucune'] });
    },
  });
}

/**
 * Un module est-il actif pour l'entreprise courante ?
 *
 * Le registre suffit pour les modules qui ont leurs propres ecrans : une route
 * inactive n'est simplement pas enregistree. Certains modules n'apportent pas
 * d'ecran a eux mais un bloc greffe sur l'ecran d'un autre — Payments sur la
 * fiche facture, Gdpr sur la fiche client. Ce bloc doit disparaitre exactement
 * comme disparaitrait une route (§3), d'ou ce test explicite.
 *
 * Pendant le chargement des activations, la reponse est `false` : on prefere ne
 * rien montrer un instant plutot que d'afficher puis retirer.
 */
export function useModuleActif(cleModule: string): boolean {
  const { data } = useModulesActives();
  return (data ?? []).some((module) => module.cleModule === cleModule && module.actif);
}

/** Detail de l'entreprise active. */
export function useEntrepriseActive() {
  const entrepriseActive = useSession((etat) => etat.entrepriseActive);

  return useQuery({
    queryKey: ['entreprise', entrepriseActive ?? 'aucune', 'detail'],
    enabled: entrepriseActive !== null,
    queryFn: () =>
      deballer(
        clientApi.GET('/v1/entreprises/{entrepriseId}', {
          params: { path: { entrepriseId: entrepriseActive as string } },
        }),
      ),
  });
}
